import { children, find } from './fmp4-split';

/** How much recent media the player keeps for clips: the longest clip a channel can allow. */
export const CLIP_RING_MS = 90_000;

/** A media segment the player appended, kept for clips. */
export interface ClipSegment {
  seq: number;
  data: ArrayBuffer;
  durationMs: number;
  /** Legacy whole segments omit this; dependent transport parts must set false. */
  keyframe?: boolean;
}

/** Drops the oldest segments, in place, while the rest still cover `maxMs`. */
export function trimClipRing(ring: { durationMs: number }[], maxMs: number = CLIP_RING_MS): void {
  let total = ring.reduce((n, s) => n + s.durationMs, 0);
  while (ring.length > 1 && total - ring[0]!.durationMs >= maxMs) total -= ring.shift()!.durationMs;
}

/**
 * The newest segments that together last at most `ms` (the backend refuses longer clips), but
 * always the newest whole segment for legacy input. Parts advance to an independent start;
 * an empty result means the retained/requested window has no decodable starting point.
 */
export function selectClipSegments<T extends { durationMs: number; keyframe?: boolean }>(ring: readonly T[], ms: number): T[] {
  let total = 0;
  let from = ring.length;
  while (from > 0 && (from === ring.length || total + ring[from - 1]!.durationMs <= ms)) {
    total += ring[--from]!.durationMs;
  }
  while (from < ring.length && ring[from]!.keyframe === false) from++;
  return ring.slice(from);
}

function readU32(b: Uint8Array, at: number): number {
  return ((b[at]! << 24) | (b[at + 1]! << 16) | (b[at + 2]! << 8) | b[at + 3]!) >>> 0;
}

function writeU32(b: Uint8Array, at: number, v: number): void {
  b[at] = (v >>> 24) & 255;
  b[at + 1] = (v >>> 16) & 255;
  b[at + 2] = (v >>> 8) & 255;
  b[at + 3] = v & 255;
}

/**
 * Copies of `fragments` with every `tfdt` baseMediaDecodeTime shifted so each track starts at 0:
 * per track, the base time of its first fragment is subtracted from all of its fragments. Every
 * top-level `moof` of a fragment is handled; all other bytes are left as they are.
 */
export function rebaseDecodeTimes(fragments: readonly Uint8Array[]): Uint8Array<ArrayBuffer>[] {
  const first = new Map<number, number>();
  return fragments.map((source) => {
    const b = new Uint8Array(source);
    for (const moof of children(b, 0, b.length)) {
      if (moof.type !== 'moof') continue;
      for (const traf of children(b, moof.body, moof.end)) {
        if (traf.type !== 'traf') continue;
        const tfhd = find(b, traf.body, traf.end, ['tfhd']);
        const tfdt = find(b, traf.body, traf.end, ['tfdt']);
        if (!tfhd || !tfdt) continue;
        const trackId = readU32(b, tfhd.body + 4);
        const at = tfdt.body + 4;
        const v1 = b[tfdt.body] === 1;
        const base = v1 ? readU32(b, at) * 2 ** 32 + readU32(b, at + 4) : readU32(b, at);
        if (!first.has(trackId)) first.set(trackId, base);
        // A track that jumps back (a timeline reset inside the clip) cannot go below 0.
        const rebased = Math.max(0, base - first.get(trackId)!);
        if (v1) {
          writeU32(b, at, Math.floor(rebased / 2 ** 32));
          writeU32(b, at + 4, rebased % 2 ** 32);
        } else {
          writeU32(b, at, rebased);
        }
      }
    }
    return b;
  });
}

/** One playable fMP4 file: the init segment, then the fragments rebased to start at 0. */
export function assembleClip(init: Uint8Array, fragments: readonly Uint8Array[]): Uint8Array<ArrayBuffer> {
  const parts = [init, ...rebaseDecodeTimes(fragments)];
  const out = new Uint8Array(parts.reduce((n, p) => n + p.length, 0));
  let at = 0;
  for (const p of parts) {
    out.set(p, at);
    at += p.length;
  }
  return out;
}
